import { format, UTCDate } from '@/utils/dates';
import { LegalEntityCountry, PayrollStatus } from '@seapoint/server/enums';
import { ColumnDef, Row, SortingFn } from '@tanstack/react-table';
import { AmountDisplay } from '../../../../design-system/amount-display/amount-display';
import { ColumnHeaderWithSorting } from '../../../../design-system/data-table/column-header-with-sorting';
import { Inline } from '../../../../design-system/inline/inline';
import { PayrollView } from '../../../../types/entity-types';
import { getCountryFlag } from '../../../../utils/get-country-flag';
import { getNormalizedAmount } from '../../../../utils/get-normalized-amount';
import { PayrollStatusBadge } from '../payroll-status-badge/payroll-status-badge';

const getStatusDisplayLabel = (status: string) => {
  switch (status) {
    case PayrollStatus.SUCCEEDED:
      return 'Paid';
    case PayrollStatus.PARTIALLY_SUCCEEDED:
      return 'Partially Paid';
    case PayrollStatus.FAILED:
      return 'Failed';
    case PayrollStatus.APPROVED:
      return 'To Pay';
    case PayrollStatus.WAITING_FOR_APPROVAL:
      return 'Needs Approval';
    case PayrollStatus.IN_DRAFT:
      return 'Draft';
    default:
      return status;
  }
};

type PayrollTableRow = PayrollView & {
  id: string;
};

const statusSortingFn: SortingFn<PayrollTableRow> = (rowA: Row<PayrollTableRow>, rowB: Row<PayrollTableRow>) => {
  const statusA = rowA.original.status;
  const statusB = rowB.original.status;

  const getStatusPriority = (status: string) => {
    switch (status) {
      case PayrollStatus.SUCCEEDED:
        return 0; // Highest priority
      case PayrollStatus.PARTIALLY_SUCCEEDED:
        return 1;
      case PayrollStatus.APPROVED:
        return 2;
      case PayrollStatus.WAITING_FOR_APPROVAL:
        return 3;
      case PayrollStatus.IN_DRAFT:
        return 4;
      case PayrollStatus.FAILED:
        return 5;
      default:
        return 6;
    }
  };

  const priorityA = getStatusPriority(statusA);
  const priorityB = getStatusPriority(statusB);

  return priorityA - priorityB;
};

// Helper function to calculate total net pay
const calculateTotalNetPay = (payroll: PayrollView) => {
  return payroll.records.reduce((total, record) => total + record.netPay, 0);
};

// Helper function to format payroll period
const formatPayrollPeriod = (period: string) => {
  try {
    // Assuming period is in format like "2024-01-01" or "January 2024"
    if (period.includes('-')) {
      const date = new UTCDate(period);
      return format(date, 'dd MMM - dd MMM');
    }
    return period;
  } catch {
    return period;
  }
};

// Helper function to format payment date
const formatPaymentDate = (payroll: PayrollView) => {
  if (payroll.status === 'SUCCEEDED' && payroll.updatedAt) {
    try {
      const date = new UTCDate(payroll.updatedAt);
      return format(date, 'dd MMM');
    } catch {
      return '—';
    }
  }
  return '—';
};

export const payrollTableColumnsDefinition: ColumnDef<PayrollTableRow>[] = [
  {
    id: 'payrollDate',
    accessorKey: 'period',
    header: ({ column }) => <ColumnHeaderWithSorting column={column} label="Payroll date" />,
    cell: ({ row }) => {
      const period = formatPayrollPeriod(row.original.period);
      return <span className="text-sm text-black">{period}</span>;
    },
    size: 16,
    minSize: 16,
  },
  {
    id: 'payrollRun',
    accessorFn: (row) => row.name,
    header: ({ column }) => <ColumnHeaderWithSorting column={column} label="Payroll Run" />,
    cell: ({ row }) => {
      const countryFlag = getCountryFlag(row.original.country as unknown as LegalEntityCountry);
      return (
        <Inline space={2} wrap="nowrap">
          <img src={countryFlag} loading="lazy" className="h-5 w-5 rounded-full object-cover" />
          <span className="text-sm text-black">{row.original.name}</span>
        </Inline>
      );
    },
    size: 25,
    minSize: 25,
  },
  {
    id: 'status',
    accessorFn: (row) => getStatusDisplayLabel(row.status),
    header: ({ column }) => <ColumnHeaderWithSorting column={column} label="Status" />,
    cell: ({ row }) => {
      const status = row.original.status;

      return <PayrollStatusBadge status={status} />;
    },
    sortingFn: statusSortingFn,
    filterFn: (row, _id, filterValue) => {
      const rowDisplayValue = getStatusDisplayLabel(row.original.status);
      return Array.isArray(filterValue) && filterValue.includes(rowDisplayValue);
    },
    size: 18,
    minSize: 18,
  },
  {
    id: 'paymentDate',
    accessorFn: (row) => formatPaymentDate(row),
    header: ({ column }) => <ColumnHeaderWithSorting column={column} label="Payment date" />,
    cell: ({ row }) => {
      const paymentDate = formatPaymentDate(row.original);
      return <span className="text-sm text-black">{paymentDate}</span>;
    },
    size: 16,
    minSize: 16,
  },
  {
    id: 'totalNetPay',
    accessorFn: (row) => calculateTotalNetPay(row).toFixed(2),
    header: ({ column }) => {
      return (
        <div className="flex justify-end">
          <ColumnHeaderWithSorting column={column} label="Total net pay" />
        </div>
      );
    },
    cell: ({ row }) => {
      const totalNetPay = calculateTotalNetPay(row.original);
      const normalizedAmount = getNormalizedAmount({ amount: totalNetPay, isCredit: false });

      return (
        <div className="flex justify-end">
          <AmountDisplay normalizedAmount={normalizedAmount} currency={row.original.currency} />
        </div>
      );
    },
    size: 15,
  },
];
